import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   VolumeMaxIcon,
} from '@forwardflow/icons'
import { ENV } from '@forwardflow/helpers/configs/envConfig'
import { useGetVoices } from '@forwardflow/queries/campaign.queries'
import { useOnboardingStoreV2 } from '@forwardflow/store'
import {
   Button,
   Checkbox,
   ContentCard,
   Grid,
   Group,
   Stack,
} from '@forwardflow/ui-v2'
import VoiceAvatar1 from '@forwardflow/assets/onboarding/voice1.png'
import VoiceAvatar2 from '@forwardflow/assets/onboarding/voice2.png'
import { motion } from 'framer-motion'
import cn from 'classnames'
import { useState, useRef, useEffect, useMemo } from 'react'

type TVoiceAssistant = {
   voiceId: string
   lastUpdated: string
   name: string
   description: string
   avatar: string
   gender: string
   previewUrl?: string
}

const ELEVENLABS_VOICES: TVoiceAssistant[] = [
   {
      voiceId: 'P7x743VjyZEOihNNygQ9',
      lastUpdated: '2025-08-26T16:04:53.948Z',
      name: 'Dakotah',
      description: 'Hello I am Dakotah H, your real state agent',
      avatar:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png',
      gender: 'female',
   },
   {
      voiceId: 'f5HLTX707KIM4SzJYzSz',
      lastUpdated: '2025-10-30T19:08:35.834Z',
      name: 'Bradward',
      description: 'male telecom',
      avatar:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-2.png',
      gender: 'male',
   },
]

const GEMINI_VOICES: TVoiceAssistant[] = [
   {
      voiceId: 'Sulafat',
      lastUpdated: '',
      name: 'Sulafat',
      description: 'Sulafat voice assistant',
      avatar:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png',
      gender: 'female',
   },
   {
      voiceId: 'Umbriel',
      lastUpdated: '',
      name: 'Umbriel',
      description: 'Umbriel voice assistant',
      avatar:
         'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-2.png',
      gender: 'male',
   },
]

const VOICE_PROVIDER = ENV.VOICE_PROVIDER
const FALLBACK_VOICES = VOICE_PROVIDER === 'gemini' ? GEMINI_VOICES : ELEVENLABS_VOICES

// const PRICES = {
//    Dakotah: '0.02/Minute',
//    Bradward: '0.025/Minute',
// }

const AVATARS = [VoiceAvatar1, VoiceAvatar2]

export const VoiceAssistant = () => {
   const {
      getNextStep,
      getPreviousStep,
      setCampaignConfiguration,
      onboardingData,
   } = useOnboardingStoreV2()
   const { data: voices } = useGetVoices()

   // Pre-select first voice if nothing is selected yet
   useEffect(() => {
      if (!onboardingData?.campaignConfiguration?.ai_assistants?.length) {
         const defaultVoice = VOICE_PROVIDER === 'gemini' ? GEMINI_VOICES[0] : ELEVENLABS_VOICES[0]
         setCampaignConfiguration({
            ai_assistants: [
               { id: 1, voice_id: defaultVoice.voiceId, assistant_name: defaultVoice.name },
            ],
         })
      }
   }, [])

   const filteredVoices: TVoiceAssistant[] = useMemo(() => {
      // Backend now curates onboarding voices via showOnOnboarding; use them
      // directly (2-up layout), falling back to the static list when empty.
      if (!voices?.length) return FALLBACK_VOICES
      return voices.slice(0, 2)
   }, [voices])

   const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
   const audioRef = useRef<HTMLAudioElement | null>(null)

   // Stop any playing audio on unmount (when navigating away)
   useEffect(() => {
      return () => {
         if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
            audioRef.current = null
         }
      }
   }, [])

   const playAudio = (voiceId: string, url: string) => {
      const audio = new Audio(url)
      audioRef.current = audio
      setPlayingVoiceId(voiceId)

      audio.onended = () => {
         setPlayingVoiceId(null)
         audioRef.current = null
      }

      audio.onerror = () => {
         setPlayingVoiceId(null)
         audioRef.current = null
      }

      audio.play()
   }

   // Preview plays the catalog preview_url directly (no ElevenLabs fetch/blob).
   const handlePlayVoice = (
      voiceId: string,
      previewUrl: string | undefined,
      e: React.MouseEvent
   ) => {
      e.stopPropagation()

      const stopCurrent = () => {
         if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
            audioRef.current = null
         }
         setPlayingVoiceId(null)
      }

      // If already playing this voice, stop it
      if (playingVoiceId === voiceId && audioRef.current) {
         stopCurrent()
         return
      }

      // Stop any currently playing audio, then play the requested preview
      stopCurrent()
      if (!previewUrl) return
      playAudio(voiceId, previewUrl)
   }

   return (
      <Stack gap={32}>
         <ContentCard className="space-y-6">
            <p className="text-white font-semibold text-lg ">
               Select Assistant Voices
            </p>

            <div className="grid grid-cols-2 gap-4">
               {filteredVoices?.map((voice, index) => {
                  const isSelected =
                     onboardingData?.campaignConfiguration?.ai_assistants?.some(
                        (assistant) => assistant.voice_id === voice.voiceId
                     )
                  return (
                     <motion.button
                        key={index}
                        className={cn(
                           'rounded-[16px] border text-left',
                           isSelected
                              ? 'border-[#9F7BFF]/40 bg-[#9F7BFF]/10'
                              : 'border-white/20 bg-white/[.02]'
                        )}
                        onClick={() =>
                           setCampaignConfiguration({
                              ai_assistants: [
                                 {
                                    id: index + 1,
                                    voice_id: voice.voiceId,
                                    assistant_name: voice.name,
                                 },
                              ],
                           })
                        }
                     >
                        <div className="border-b border-white/20 p-6 flex flex-col items-center gap-4">
                           <div className="relative">
                              <img
                                 src={AVATARS[index]}
                                 alt={voice.name}
                                 className={cn(
                                    'w-60 relative z-10 transition-all duration-300',
                                    playingVoiceId === voice.voiceId &&
                                       'scale-105'
                                 )}
                              />
                              {playingVoiceId === voice.voiceId && (
                                 <>
                                    <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-2xl opacity-60 animate-pulse pointer-events-none" />
                                    <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-3xl opacity-40 animate-ping pointer-events-none" />
                                 </>
                              )}
                           </div>
                           <p className="text-white font-semibold text-lg">
                              {voice.name}
                           </p>
                           <button
                              className={cn(
                                 'rounded-[16px] flex items-center gap-2 border p-2 px-4 text-white transition-all cursor-pointer hover:bg-white/[.08]',
                                 playingVoiceId === voice.voiceId
                                    ? 'border-[#9F7BFF] bg-[#9F7BFF]/20'
                                    : 'border-white/15 bg-white/[.02]'
                              )}
                              onClick={(e) => {
                                 handlePlayVoice(voice.voiceId, voice.previewUrl, e)
                              }}
                           >
                              <VolumeMaxIcon width={18} />
                              {playingVoiceId === voice.voiceId
                                 ? 'Stop Voice'
                                 : 'Play Voice'}
                           </button>
                           {/* <p className="text-white/70 text-sm">
                              ${PRICES[voice.name as keyof typeof PRICES]}
                           </p> */}
                        </div>
                        <Group className="p-6" justify="center">
                           <Checkbox size="sm" checked={isSelected} readOnly />
                           <p className="text-white font-semibold">
                              Select Voice
                           </p>
                        </Group>
                     </motion.button>
                  )
               })}
            </div>
         </ContentCard>
         <Grid>
            <Grid.Col span={3}>
               <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => getPreviousStep()}
               >
                  <ArrowLeftMdIcon width={12} /> Back
               </Button>
            </Grid.Col>
            <Grid.Col span={9}>
               <Button
                  className="w-full"
                  onClick={() => getNextStep()}
                  disabled={
                     !onboardingData?.campaignConfiguration?.ai_assistants
                        ?.length
                  }
               >
                  Confirm and Continue <ChevronRightIcon width={18} />
               </Button>
            </Grid.Col>
         </Grid>
      </Stack>
   )
}
